import { ReferralConfig } from '@winelivery-org/wl-client-sdk-js';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import TripleDropSpinner from '../../../../components/commons/TripleDropSpinner';
import { Button } from '../../../../components/design-system';
import { Cork } from '../../../../components/icons';
import { useToggle } from '../../../../hooks';
import { useRouter } from '../../../../navigation';
import useSession from '../../../../stores/session/useSession';
import Modal from './Modal';
import Code from './Modal/Code';
import ModalContainerError from './Modal/ModalContentError';
import Share from './Modal/Share';
import useRegisterToReferralProgram from './query/useRegisterToReferralProgram';

interface Props {
  referralCode?: string;
  referralConfig: ReferralConfig;
}

export default function SectionShare(props: Props) {
  const { isOpen, close, open } = useToggle();
  const money = props.referralConfig.firstOrderDiscount || 10;
  const t = useTranslations('loyalty');
  const router = useRouter();
  const { user } = useSession();

  const { mutateAsync, isPending } = useRegisterToReferralProgram();

  const [code, setCode] = useState(props.referralCode);

  const onShare = async () => {
    if (!user) {
      return router.push({
        pathname: '/auth/signin',
        query: { redirect: window.location.href },
      });
    }

    try {
      const result = await mutateAsync(user._id);
      setCode(result.data.referral!.referralCode);
    } catch (err) {
      open();
    }
  };

  return (
    <div className="tw-mb-16 tw-max-w-5xl tw-mx-auto tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-6 tw-px-4">
      <h3 className="tw-text-4xl tw-font-bold tw-text-greyscale-title tw-tracking-tighter tw-m-0 tw-p-0">
        {t('wallet.referral.title')}
      </h3>
      <p className="tw-text-base tw-font-medium tw-tracking-tight tw-text-greyscale-body tw-text-center tw-m-0 tw-p-0">
        {t('wallet.referral.description', { value: money })}
      </p>
      <div className="tw-mx-auto tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-items-start tw-justify-center tw-gap-8 tw-py-4">
        <div>
          <div className="tw-bg-greyscale-offwhite tw-border tw-border-greyscale-separator tw-rounded-2xl tw-px-3 tw-py-4 tw-flex tw-flex-col tw-items-center tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-1">
              <h6 className="tw-text-3xl tw-font-bold tw-tracking-tighter tw-text-primary-dark tw-m-0">
                {props.referralConfig.signupReward}
              </h6>
              <Cork className="tw-text-primary-dark" />
            </div>
            <p className="tw-text-center tw-text-base tw-font-medium tw-tracking-tight tw-text-primary-dark tw-m-0 tw-p-0">
              {t('wallet.referral.signUpReward')}
            </p>
          </div>
        </div>
        <div>
          <div className="tw-bg-greyscale-offwhite tw-border tw-border-greyscale-separator tw-rounded-2xl tw-px-3 tw-py-4 tw-flex tw-flex-col tw-items-center tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-1">
              <h6 className="tw-text-3xl tw-font-bold tw-tracking-tighter tw-text-primary-dark tw-m-0">
                {props.referralConfig.firstOrderReward}
              </h6>
              <Cork className="tw-text-primary-dark" />
            </div>
            <p className="tw-text-center tw-text-base tw-font-medium tw-tracking-tight tw-text-primary-dark tw-m-0 tw-p-0">
              {t('wallet.referral.firstOrderReward')}
            </p>
          </div>
        </div>
        <div>
          <div className="tw-bg-greyscale-offwhite tw-border tw-border-greyscale-separator tw-rounded-2xl tw-px-3 tw-py-4 tw-flex tw-flex-col tw-items-center tw-gap-2">
            <h6 className="tw-text-3xl tw-font-bold tw-tracking-tighter tw-text-primary-dark tw-m-0">
              {money}€
            </h6>
            <p className="tw-text-center tw-text-base tw-font-medium tw-tracking-tight tw-text-primary-dark tw-m-0 tw-p-0">
              {t('wallet.referral.firstOrderDiscount')}
            </p>
          </div>
        </div>
      </div>
      {code ? (
        <div>
          <Code referralCode={code} />

          <Share
            referralCode={code}
            firstOrderDiscount={props.referralConfig.firstOrderDiscount}
          />
        </div>
      ) : (
        <div className="tw-text-center">
          <Button size="lg" className="tw-w-60" onClick={onShare}>
            {t('wallet.referral.button')}
          </Button>
        </div>
      )}
      {isPending && <TripleDropSpinner />}

      <Modal isOpen={isOpen} onRequestClose={close}>
        <ModalContainerError onClick={close} />
      </Modal>
    </div>
  );
}
